import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQueryClient } from 'react-query';
import { Prompt, useParams } from 'react-router-dom';
import { isSameDay } from 'date-fns';

import { IField } from '@models/Field.model';
import { ICustomField } from '@models/CustomField.model';

import { updateCustomFieldContent } from '@services/queries/customField';
import { IEquipmentWithoutProduct } from '@models/Equipment.model';
import ReactTooltip from 'react-tooltip';
import { ITooltip } from '@components/Tooltip';
import { getPermissionData } from '@utils';
import { NavigationContext } from '@layout/navigation/navigation';
import AppError from '@utils/AppError';
import { UpdateContentError } from '@services/queries/customField/updateCustomFieldContent/updateCustomFieldContent';
import { ErrorText } from '@pages/ConfigurationPage/Components/FieldsManagement/Components/CustomFields/Components/CustomFieldsTab/CustomFieldsTab.styles';
import { setAttr } from '@utils/setAttr';
import { trackAssetEdited } from '@services/mixpanel/trackers/AssetMetrics';
import { AssetContext } from '@pages/Asset/Asset.query';
import {
  BigEditableInput,
  Buttons,
  Cancel,
  Done,
} from './EditableCustomField.styles';
import { EditableSelect } from './components';
import { DateInputAssets } from '../DateInputAssets/DateInputAssets';
import { StyledContentEditable } from '../EditableField/EditableField.styles';

interface InfoEditableProps {
  fieldContent?: IField;
  equipment: IEquipmentWithoutProduct;
  customField: ICustomField;
  pinnedFields?: boolean;
}
// Todo: Decouple inputs from the mutation.
const EditableCustomField = ({
  fieldContent,
  customField,
  equipment,
  pinnedFields,
}: InfoEditableProps) => {
  const params = useParams<{ equipmentId: string }>();

  const { role } = useContext(NavigationContext);

  const inputRef = useRef<HTMLInputElement>(null);
  const inputBigRef = useRef<HTMLTextAreaElement>(null);

  const fieldContentOrDefault =
    fieldContent?.content || customField.defaultValue;
  const date =
    customField.type === 'date' && fieldContent?.content
      ? new Date(Date.parse(fieldContent.content))
      : null;

  const [initialValue, setInitialValue] = useState(fieldContentOrDefault);
  const [value, setValue] = useState(fieldContentOrDefault);
  const [dateValue, setDateValue] = useState<Date | null>(date);
  const [initialDateValue, setInitialDateValue] = useState<Date | null>(date);
  const [showOptions, setShowOptions] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const { equipmentQuery, isEditingField, setIsEditingField } =
    useContext(AssetContext);
  const { product } = equipmentQuery?.data || {};

  useEffect(() => {
    setInitialValue(fieldContentOrDefault);
    setValue(fieldContentOrDefault);
  }, [fieldContentOrDefault]);

  const updateCustomFieldContentMutation = useMutation(
    updateCustomFieldContent,
    {
      onSuccess: updatedFieldContent => {
        const updatedEquipment = equipment;
        const updatedFields = equipment.fields
          .filter(field => field.id !== updatedFieldContent.id)
          .concat([updatedFieldContent]);
        updatedEquipment.fields = updatedFields;

        queryClient.invalidateQueries(['equipments', params.equipmentId]);
        setIsEditingField('');

        if (customField.type === 'text') {
          inputRef.current?.blur();
        }

        if (customField.type === 'description') {
          inputBigRef.current?.blur();
          if (!value?.trim()) {
            setValue('');
          }
        }

        const { name, model, manufacturer } = product || {};
        const { name: manufacturerName } = manufacturer || {};

        trackAssetEdited(
          model,
          manufacturerName,
          customField.name,
          params.equipmentId,
          name,
          'Custom Field',
        );
      },
      // For inference
      onError(_: UpdateContentError | AppError) {
        // Rollback
        setInitialValue(fieldContentOrDefault);
        setInitialDateValue(date);
      },
    },
  );

  const editPermission = getPermissionData({
    action: 'edit',
    owner: true,
    role,
    type: 'Asset Data',
  });

  const { message, allowed } = editPermission;

  const tooltipText = allowed ? '' : message;

  const handleClickIn = (e: React.MouseEvent) => {
    if (allowed) {
      setShowOptions(customField.selectableValues);
      setIsEditingField(customField.id);
    } else {
      e.stopPropagation();
    }
  };

  function cancel() {
    setValue(initialValue);
    setDateValue(initialDateValue);
    setIsEditingField('');
    setShowOptions([]);
    updateCustomFieldContentMutation.reset();
    setIsDirty(false);
  }

  const submit = () => {
    setShowOptions([]);
    setIsDirty(false);
    setInitialValue(value);
    setInitialDateValue(dateValue);
    updateCustomFieldContentMutation.mutate({
      customFieldId: customField.id,
      data: {
        equipmentId: params.equipmentId,
        content: customField.type === 'date' ? dateValue : value?.trim(),
      },
    });
  };

  const handleOnDateChange = (event: any) => {
    if (event === null) setDateValue(event);
    const test = Date.parse(event);

    if (test) {
      setDateValue(new Date(test));
      setIsDirty(true);
    }
  };

  const handleOnChange = (event: any) => {
    const {
      target: { value: _value },
    } = event;

    setValue(_value);
    setIsDirty(true);
  };

  const handleOnChangeSelect = (event: any) => {
    setValue(event);
    setShowOptions([]);
    setIsDirty(true);
  };

  const submitDisabled = useMemo(() => {
    if (updateCustomFieldContentMutation.isLoading) {
      return true;
    }

    if (customField.type === 'date') {
      if (dateValue) {
        return isSameDay(dateValue as Date, initialDateValue as Date);
      }

      return dateValue === initialDateValue;
    }

    if (customField.type === 'description') {
      return value?.trim() === initialValue?.trim();
    }

    return value === initialValue;
  }, [
    value,
    initialValue,
    dateValue,
    initialDateValue,
    updateCustomFieldContentMutation.isLoading,
  ]);

  const { variables, error } = updateCustomFieldContentMutation;
  const hasAppError = error instanceof AppError;
  let sameValue = variables?.data.content === value?.trim();
  if (customField.type === 'date') {
    sameValue = variables?.data.content === dateValue;
  }
  const showError = hasAppError && sameValue;
  const errorMsg = error?.message;
  return (
    <div
      data-tip={tooltipText}
      data-for={`editAssetDataCustomField-${customField.id}`}
      data-event={allowed ? undefined : 'click'}
    >
      {customField.type === 'date' && (
        <DateInputAssets
          handleDateChange={handleOnDateChange}
          value={dateValue}
          divProps={{
            'data-active': isEditingField === customField.id,
            'data-error': showError,
            onClick: handleClickIn,
          }}
          pinnedData={pinnedFields}
          direction={pinnedFields ? 'rtl' : ''}
          disabled={!allowed}
        />
      )}

      {customField.type === 'select' && (
        <EditableSelect
          showOptions={showOptions}
          searchText={value ?? ''}
          handleClickSearch={handleClickIn}
          active={isEditingField === customField.id}
          onClickFilter={input => handleOnChangeSelect(input)}
          disabled={!allowed}
          data-error={showError}
          pinnedFields={pinnedFields}
        />
      )}

      {customField.type === 'text' && (
        <>
          {allowed ? (
            <StyledContentEditable
              ref={inputRef}
              onChange={handleOnChange}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter' && !submitDisabled) {
                  submit();
                }
              }}
              onClick={handleClickIn}
              value={value ?? ''}
              active={isEditingField === customField.id}
              placeholder="Empty"
              disabled={setAttr(!allowed)}
              data-error={showError}
              pinnedFields={pinnedFields}
            />
          ) : (
            <div>
              <StyledContentEditable
                ref={inputRef}
                onChange={handleOnChange}
                onKeyDown={(e: any) => {
                  if (e.key === 'Enter' && !submitDisabled) {
                    submit();
                  }
                }}
                onClick={handleClickIn}
                value={value ?? ''}
                active={isEditingField === customField.id}
                disabled={setAttr(!allowed)}
                placeholder="Empty"
                pinnedFields={pinnedFields}
              />
            </div>
          )}
        </>
      )}

      {customField.type === 'description' && (
        <>
          {allowed ? (
            <BigEditableInput
              ref={inputBigRef}
              onChange={handleOnChange}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter' && !e.shiftKey && !submitDisabled) {
                  submit();
                }
              }}
              onClick={handleClickIn}
              role="textbox"
              value={value ?? ''}
              active={isEditingField === customField.id}
              placeholder="Empty"
              data-error={showError}
              disabled={setAttr(!allowed)}
              pinnedFields={pinnedFields}
            />
          ) : (
            <div>
              <BigEditableInput
                ref={inputBigRef}
                onChange={handleOnChange}
                onKeyDown={(e: any) => {
                  if (e.key === 'Enter' && !e.shiftKey && !submitDisabled) {
                    submit();
                  }
                }}
                onClick={handleClickIn}
                role="textbox"
                value={value ?? ''}
                active={isEditingField === customField.id}
                disabled={setAttr(!allowed)}
                placeholder="Empty"
                pinnedFields={pinnedFields}
              />
            </div>
          )}
        </>
      )}

      <ITooltip
        id={`editAssetDataCustomField-${customField.id}`}
        globalEventOff="click"
        disable={setAttr(allowed)}
        afterShow={() => {
          setTimeout(ReactTooltip.hide, 1000);
        }}
      />
      {showError && <ErrorText className="truncated">{errorMsg}</ErrorText>}
      {isEditingField === customField.id && (
        <Buttons>
          <Cancel onClick={cancel} type="button">
            Cancel
          </Cancel>
          <Done onClick={submit} type="button" disabled={submitDisabled}>
            Done
          </Done>
        </Buttons>
      )}

      <Prompt
        when={isDirty}
        message="You have unsaved changes, Are you sure you want to leave?"
      />
    </div>
  );
};
export { EditableCustomField };
